import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Lock, Eye, EyeOff, Phone, ChevronDown, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Footer from "@/components/Footer";

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    gender: "",
    dob: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!acceptTerms) {
      toast.error("Please accept the Terms & Conditions");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(formData.name, formData.email || formData.phone, formData.password);
      toast.success("Account created! Let's set up your profile.");
      navigate("/setup");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 px-4 py-4 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch Logo" 
              className="w-10 h-10 rounded-xl object-cover"
            />
            <span className="text-xl font-bold">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-gray-600 text-sm hidden sm:block">Already have an account?</span>
            <button 
              onClick={() => navigate("/login")}
              className="bg-gradient-to-r from-[#FAB710] to-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-[#1C3A7A]">Create Account</h1>
            <p className="text-gray-500 mt-2">Join the recovery community</p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-lg p-6 sm:p-8">
            {/* Legal Text */}
            <p className="text-center text-gray-500 text-sm mb-6">
              By creating an account, you agree to our{" "}
              <Link to="/terms" className="text-[#1C3A7A] underline hover:text-[#FAB710]">Terms</Link>,{" "}
              <Link to="/privacy" className="text-[#1C3A7A] underline hover:text-[#FAB710]">Privacy Policy</Link>, and{" "}
              <Link to="/cookies" className="text-[#1C3A7A] underline hover:text-[#FAB710]">Cookie Policy</Link>.
            </p>

            {/* Social Login Buttons */}
            <div className="space-y-3 mb-6">
              <button 
                type="button"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + '/auth-callback';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full flex items-center justify-center gap-3 bg-[#4285F4] hover:bg-[#357ABD] text-white py-3.5 px-6 rounded-full font-semibold transition-all hover:shadow-lg"
              >
                <svg className="w-5 h-5 bg-white rounded-full p-0.5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              {/* Facebook - Coming Soon (waiting for custom domain) */}
              <button 
                type="button"
                disabled
                className="w-full flex items-center justify-center gap-3 bg-gray-100 border-2 border-gray-200 text-gray-400 py-3.5 px-6 rounded-full font-semibold cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#9CA3AF">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook — Coming Soon
              </button>

              <button 
                type="button"
                disabled
                className="w-full flex items-center justify-center gap-3 bg-gray-100 border-2 border-gray-200 text-gray-400 py-3.5 px-6 rounded-full font-semibold cursor-not-allowed"
              >
                <Phone className="w-5 h-5" />
                Phone Login — Coming Soon
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-400">or register with email</span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <User className="h-5 w-5" />
                </div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Full name"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-12 h-14 bg-gray-50 border-gray-200 rounded-full text-base"
                  required
                />
              </div>

              {/* Email */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <Mail className="h-5 w-5" />
                </div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-12 h-14 bg-gray-50 border-gray-200 rounded-full text-base"
                  required
                />
              </div>

              {/* Gender & DOB Row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full h-14 pl-4 pr-10 bg-gray-50 border border-gray-200 rounded-full appearance-none text-gray-700 focus:ring-[#FAB710] focus:border-[#FAB710]"
                    required
                  >
                    <option value="">Gender</option>
                    <option value="Man">Man</option>
                    <option value="Woman">Woman</option>
                    <option value="Transgender Man">Transgender Man</option>
                    <option value="Transgender Woman">Transgender Woman</option>
                    <option value="Non-binary">Non-binary</option>
                    <option value="Genderqueer">Genderqueer</option>
                    <option value="Genderfluid">Genderfluid</option>
                    <option value="Agender">Agender</option>
                    <option value="Two-Spirit">Two-Spirit</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
                <div>
                  <Input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    className="h-14 bg-gray-50 border-gray-200 rounded-full text-gray-700"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <Phone className="h-5 w-5" />
                </div>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone number (optional)"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-12 h-14 bg-gray-50 border-gray-200 rounded-full text-base"
                />
              </div>

              {/* Password */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock className="h-5 w-5" />
                </div>
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Create password"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-12 pr-12 h-14 bg-gray-50 border-gray-200 rounded-full text-base"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock className="h-5 w-5" />
                </div>
                <Input
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-12 h-14 bg-gray-50 border-gray-200 rounded-full text-base"
                  required
                />
              </div>

              {/* Terms */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#FAB710] focus:ring-[#FAB710]"
                  data-testid="terms-checkbox"
                />
                <label htmlFor="terms" className="text-sm text-gray-600">
                  I agree to the{" "}
                  <Link to="/terms" className="text-[#1C3A7A] hover:text-[#FAB710] underline">
                    Terms & Conditions
                  </Link>
                  {" "}and{" "}
                  <Link to="/privacy" className="text-[#1C3A7A] hover:text-[#FAB710] underline">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-14 bg-gradient-to-r from-[#FAB710] to-[#FAB710] hover:shadow-lg hover:shadow-orange-200 text-white text-lg font-semibold rounded-full transition-all"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating account...
                  </div>
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>

            {/* Login Link - Mobile */}
            <div className="mt-6 text-center sm:hidden">
              <p className="text-gray-600">
                Already have an account?{" "}
                <Link to="/login" className="text-[#1C3A7A] font-semibold hover:text-[#FAB710] hover:underline">
                  Sign In
                </Link>
              </p>
            </div>
          </div>

          {/* Get the App */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm mb-3">Get the app!</p>
            <div className="flex items-center justify-center gap-3">
              <a href="#" className="hover:scale-105 transition-transform">
                <img 
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                  alt="Download on App Store" 
                  className="h-10"
                />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.recoverymatch.dating" target="_blank" rel="noopener noreferrer" className="hover:scale-105 transition-transform">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                  alt="Get it on Google Play" 
                  className="h-10"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterPage;
